"use client";

import { TicketItem } from "@features/cartDetails/types";
import { useLatestCookieValue, useThemeContext } from "@utils/hooks";
import { useSearchParams } from "next/navigation";
import useSWR from "swr";
import { GATicket } from "@features/eventPage/types";
import EventGASelection from "../components/eventGASelection";
import { customFetcher } from "../utils/customFetcher";
import { filterAndMapTicketOptions } from "../utils/ticketOptions";
import { Card, Heading, Text } from "@components/theming";
import { useTranslations } from "next-intl";
import LoadingSpinner from "@components/loadingSpinner";
import { useEffect } from "react";
import SkeletonLoading from "../components/skeletonLoading";
import { useIsShopsite } from "@context/DomainContext";
import { Analytics, EventLandingPage } from "@types";
import { handle429s } from "@utils/fetchers";

interface Props {
  eventId: number;
  eventName: string;
  eventVenueId: number;
  orgUid: string;
  showingId: number;
  selectedConnectedShowings: number[] | null;
  gaAreaIds: number[];
  priceSectionIds: number[];
  isExtraAddOns?: boolean;
  basketTickets: TicketItem[];
  createBasket?: () => Promise<void>;
  analyticsConfigs?: Analytics[];
  landingPages?: EventLandingPage[];
  onAddToCart?: () => void;
}

export default function DisplayGA({
  eventId,
  eventName,
  eventVenueId,
  orgUid,
  showingId,
  selectedConnectedShowings,
  gaAreaIds,
  priceSectionIds,
  isExtraAddOns = false,
  basketTickets,
  createBasket,
  analyticsConfigs,
  landingPages,
  onAddToCart,
}: Props) {
  const t = useTranslations("EventPage.displayga");
  const session_params = useLatestCookieValue("itk_sessionparams");
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const params = useSearchParams();
  const promo = params.get("promo");
  const isShopsite = useIsShopsite();
  const theme = useThemeContext();
  const { data, isLoading, error } = useSWR<GATicket>(
    basketKey
      ? [
          `events/${eventId}/${eventVenueId}/showings/${showingId}/tickets/ga${
            promo ? `?promo=${promo}` : ""
          }${
            selectedConnectedShowings && selectedConnectedShowings.length > 0
              ? `${
                  promo ? "&" : "?"
                }connectedShowings=[${selectedConnectedShowings
                  .sort((a, b) => a - b)
                  .join(",")}]`
              : ""
          }`,
          session_params ?? "",
        ]
      : null,
    customFetcher,
    {
      revalidateOnFocus: false,
      onErrorRetry: handle429s,
    }
  );

  useEffect(() => {
    if (!basketKey && createBasket) {
      createBasket();
    }
  }, [basketKey, createBasket]);

  const prices = filterAndMapTicketOptions(
    data,
    gaAreaIds,
    priceSectionIds,
    isExtraAddOns,
    basketTickets
  );

  if (isLoading && !data && !error) {
    return <SkeletonLoading />;
  }

  if (!data && error && error.response.status === 429) {
    return (
      <Card shopsiteClasses="border border-gray-200 rounded-lg">
        <div className="flex flex-col items-center justify-center gap-4 w-full h-[197px]">
          <div className="flex flex-col gap-1">
            <Heading
              as="h3"
              size="6"
              weight="bold"
              sharedClasses="text-center mx-4"
              shopsiteClasses="font-bold text-2xl"
            >
              {t("error.retryheading")}
            </Heading>
            <Text
              size="5"
              weight="bold"
              whitelabelClasses="text-[color:var(--accent-11)]"
              sharedClasses="text-center"
              shopsiteClasses="text-base text-gray-500"
            >
              {t("error.retrymessage")}
            </Text>
          </div>
          <LoadingSpinner
            size="42px"
            color={
              isShopsite
                ? "#6366f1"
                : theme && theme.appearance === "dark"
                ? "#fff"
                : "#000"
            }
          />
        </div>
      </Card>
    );
  }

  if (!data && error) {
    return (
      <Card shopsiteClasses="border border-gray-200 rounded-lg">
        <div className="flex flex-col items-center justify-center w-full h-[197px]">
          <div className="relative mx-4">
            <Heading
              as="h3"
              size="6"
              weight="bold"
              sharedClasses="text-center relative z-10"
              shopsiteClasses="text-lg font-semibold"
            >
              {t("error.generic")}
            </Heading>
          </div>
        </div>
      </Card>
    );
  }

  if (!prices || prices.length === 0) {
    return (
      <Card shopsiteClasses="max-sm:-mx-4 max-sm:-my-2 border bg-white border-gray-200 sm:rounded-lg overflow-hidden relative">
        <div className="flex flex-col items-center justify-center w-full h-[197px]">
          <div className="relative mx-4">
            <Heading
              as="h3"
              size="6"
              weight="bold"
              sharedClasses="text-center relative z-10 mt-2"
              shopsiteClasses="font-bold text-2xl max-w-xl"
            >
              {t("error.message")}
            </Heading>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <EventGASelection
      basketTickets={basketTickets}
      eventId={eventId}
      eventName={eventName}
      eventVenueId={eventVenueId}
      orgUid={orgUid}
      showingId={showingId}
      selectedConnectedShowings={selectedConnectedShowings}
      prices={prices}
      analyticsConfigs={analyticsConfigs}
      landingPages={landingPages}
      onAddToCart={onAddToCart}
    />
  );
}
